import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Card,
  CardMedia,
  CardContent,
  Skeleton,
  Alert,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import AuthenticatedImage from '../../common/AuthenticatedImage';
import api from '../../../services/api';
import axios from 'axios';

// Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Defect Location Map Component (single location) - Memoized to prevent re-renders during PDF capture
const DefectLocationMap = React.memo(({ photo, defect }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!photo?.latitude || !photo?.longitude || !mapRef.current || mapInstanceRef.current) {
      return;
    }

    const lat = parseFloat(photo.latitude);
    const lng = parseFloat(photo.longitude);

    // Initialize map
    const map = L.map(mapRef.current, {
      center: [lat, lng],
      zoom: 17,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false
    });

    // Add ArcGIS World Imagery satellite layer with CORS enabled
    // maxNativeZoom ensures Leaflet upscales lower-zoom tiles instead of showing
    // "Map data not yet available" in areas without high-zoom satellite coverage
    const arcgisLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      maxNativeZoom: 17,
      crossOrigin: 'anonymous',
      errorTileUrl: ''
    }).addTo(map);

    // Fallback: if ArcGIS tiles fail to load, switch to OpenStreetMap
    let fallbackAdded = false;
    arcgisLayer.on('tileerror', () => {
      if (!fallbackAdded) {
        fallbackAdded = true;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          crossOrigin: 'anonymous'
        }).addTo(map);
      }
    });

    // Calculate direction - prioritize ENVIS heading_degrees, fallback to drone gimbal/flight yaw
    let direction = defect?.heading_degrees ?? photo.gimbal_yaw ?? photo.flight_yaw ?? 0;
    if (typeof direction === 'string') {
      direction = parseFloat(direction);
    }
    direction = ((direction % 360) + 360) % 360;

    // Create custom directional marker icon
    const markerIcon = L.divIcon({
      className: 'custom-direction-marker',
      html: `
        <div style="position: relative; width: 50px; height: 50px; transform: rotate(${direction}deg);">
          <div style="width: 40px; height: 40px; background-color: rgba(255, 215, 0, 0.7); border: 3px solid #FFA500; border-radius: 50%; position: absolute; top: 5px; left: 5px;"></div>
          <div style="position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 10px solid transparent; border-right: 10px solid transparent; border-bottom: 25px solid #FF4500;"></div>
        </div>
      `,
      iconSize: [50, 50],
      iconAnchor: [25, 25]
    });

    L.marker([lat, lng], { icon: markerIcon }).addTo(map);
    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [photo, defect]);

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 240, borderRadius: '4px', overflow: 'hidden', position: 'relative', bgcolor: 'transparent' }}>
      <Box ref={mapRef} sx={{ width: '100%', height: '100%', minHeight: 240, borderRadius: '4px', overflow: 'hidden', bgcolor: 'transparent' }} />
      <Paper elevation={2} sx={{ position: 'absolute', bottom: 6, right: 6, p: 0.75, bgcolor: 'rgba(255, 255, 255, 0.95)', borderRadius: 0.75, border: '1px solid rgba(0, 0, 0, 0.08)', zIndex: 1000, minWidth: 140 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.5, display: 'block', color: '#003366' }}>Legend</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'rgba(255, 215, 0, 0.7)', border: '1.5px solid #FFA500', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.primary', lineHeight: 1.1 }}>Camera Location</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 16, height: 16, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '12px solid #FF4500' }} />
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.primary', lineHeight: 1.1 }}>Camera Heading</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
});

// All Defects Overview Map Component (multiple locations) - Memoized to prevent re-renders during PDF capture
const AllDefectsOverviewMap = React.memo(({ defects, photos }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!defects || defects.length === 0 || !mapRef.current || mapInstanceRef.current) {
      return;
    }

    // Collect all valid GPS coordinates from photos
    const locations = [];
    defects.forEach((defect, index) => {
      let photo = null;
      if (defect.photo_id) {
        photo = photos?.find(p => p.id === defect.photo_id);
      } else if (defect.original_image_path) {
        const normalizedDefectPath = defect.original_image_path.replace(/\\/g, '/');
        photo = photos?.find(p => p.photo_path === normalizedDefectPath);
      }

      if (photo?.latitude && photo?.longitude) {
        const lat = parseFloat(photo.latitude);
        const lng = parseFloat(photo.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          locations.push({
            lat, lng,
            defectNumber: index + 1,
            defectType: defect.defect_type_name || 'Unknown',
            direction: defect.heading_degrees ?? photo?.gimbal_yaw ?? photo?.flight_yaw ?? 0
          });
        }
      }
    });

    if (locations.length === 0) return;

    // Calculate center
    const latitudes = locations.map(loc => loc.lat);
    const longitudes = locations.map(loc => loc.lng);
    const centerLat = (Math.max(...latitudes) + Math.min(...latitudes)) / 2;
    const centerLng = (Math.max(...longitudes) + Math.min(...longitudes)) / 2;

    // Calculate bounds
    const bounds = L.latLngBounds(locations.map(loc => [loc.lat, loc.lng]));

    // Initialize map
    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false
    });

    // Fit to bounds FIRST before adding tile layer
    if (locations.length > 1) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 17, animate: false });
    } else {
      map.setView([centerLat, centerLng], 16, { animate: false });
    }

    // Add ArcGIS World Imagery satellite layer with CORS enabled
    // maxNativeZoom ensures Leaflet upscales lower-zoom tiles instead of showing
    // "Map data not yet available" in areas without high-zoom satellite coverage
    const arcgisLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      maxNativeZoom: 17,
      crossOrigin: 'anonymous',
      errorTileUrl: ''
    }).addTo(map);

    // Fallback: if ArcGIS tiles fail to load, switch to OpenStreetMap
    let fallbackAdded = false;
    arcgisLayer.on('tileerror', () => {
      if (!fallbackAdded) {
        fallbackAdded = true;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          crossOrigin: 'anonymous'
        }).addTo(map);
      }
    });

    // Add markers for each location
    locations.forEach((location) => {
      let direction = location.direction;
      if (typeof direction === 'string') direction = parseFloat(direction);
      direction = ((direction % 360) + 360) % 360;

      const markerIcon = L.divIcon({
        className: 'custom-overview-marker',
        html: `
          <div style="position: relative; width: 20px; height: 20px; transform: rotate(${direction}deg);">
            <div style="width: 16px; height: 16px; background-color: rgba(255, 215, 0, 0.8); border: 1.5px solid #FFA500; border-radius: 50%; position: absolute; top: 2px; left: 2px; z-index: 1;"></div>
            <div style="position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 4px solid transparent; border-right: 4px solid transparent; border-bottom: 10px solid #FF4500; z-index: 2;"></div>
            <div style="position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; transform: rotate(-${direction}deg); z-index: 3;">
              <span style="color: #000; font-weight: bold; font-size: 6px; font-family: Arial, sans-serif;">${location.defectNumber}</span>
            </div>
          </div>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });

      L.marker([location.lat, location.lng], { icon: markerIcon }).addTo(map);
    });

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [defects, photos]);

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 350, borderRadius: '4px', overflow: 'hidden', position: 'relative', bgcolor: 'transparent' }}>
      <Box ref={mapRef} sx={{ width: '100%', height: '100%', minHeight: 350, borderRadius: '4px', overflow: 'hidden', bgcolor: 'transparent' }} />
      <Paper elevation={2} sx={{ position: 'absolute', bottom: 6, right: 6, p: 0.75, bgcolor: 'rgba(255, 255, 255, 0.95)', borderRadius: 0.75, border: '1px solid rgba(0, 0, 0, 0.08)', zIndex: 1000, minWidth: 140 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.5, display: 'block', color: '#003366' }}>Legend</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'rgba(255, 215, 0, 0.7)', border: '1.5px solid #FFA500', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.primary', lineHeight: 1.1 }}>Camera Location</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 16, height: 16, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '12px solid #FF4500' }} />
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.primary', lineHeight: 1.1 }}>Camera Heading</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
});

// PDF Report component using client-side jsPDF
const PDFReport = ({ inspection, defectReport, photos, severityLevels, assetType, hideDownloadButton = false, onDownloadRef, onLoadingChange, onProgressChange, onReady, reportSummary, reportTitle = "Road Inspection Report", reportDate, fullPage = false, assetDisplayImage = null }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const [localImageUrls, setLocalImageUrls] = useState({});
  const [savingToDb, setSavingToDb] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0, phase: '' });
  // Anchor element for the download options menu (single-button UX)
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const reportRef = useRef(null);

  // Defect marking on display image
  const [defectMarkers, setDefectMarkers] = useState({}); // { [defectId]: { x: %, y: % } }
  const [markingMode, setMarkingMode] = useState(false);
  const [defectPickerAnchor, setDefectPickerAnchor] = useState(null); // { mouseX, mouseY, x, y }

  // Increase photos per page to better utilize space with 4 rows
  const PHOTOS_PER_PAGE = 12;

  // Fetch and cache images as local object URLs
  useEffect(() => {
    const fetchAndCacheImages = async () => {
      if (!defectReport?.data?.photosWithDefects) return;

      // Build combined list of all unique image paths (defect photos + repeated finding images)
      const allImagePaths = new Set();
      const photosToLoad = defectReport.data.photosWithDefects;
      photosToLoad.forEach(photo => {
        if (photo.photo_path) allImagePaths.add(photo.photo_path);
      });

      // Add repeated finding images
      const repeatedFindingsMap = defectReport.data?.repeatedFindingsMap || {};
      Object.values(repeatedFindingsMap).forEach(findings => {
        findings.forEach(rf => {
          if (rf.image_path) {
            const raw = rf.image_path.replace(/\\/g, '/');
            const path = raw.startsWith('/') ? raw.slice(1) : raw;
            allImagePaths.add(path);
          }
        });
      });

      // Add asset display image if provided
      if (assetDisplayImage) {
        const displayPath = assetDisplayImage.replace(/\\/g, '/');
        allImagePaths.add(displayPath.startsWith('/') ? displayPath.slice(1) : displayPath);
      }

      const uniquePaths = Array.from(allImagePaths);
      setTotalImages(uniquePaths.length);
      setImagesLoaded(0);
      setAllImagesLoaded(false);
      setLocalImageUrls({});

      const urls = {};
      const promises = uniquePaths.map(async (photoPath) => {
        try {
          const presignedUrlResponse = await api.post(`/uploads/get-secure-url`, { path: photoPath });
          const presignedUrl = presignedUrlResponse.data.url;
          const imageResponse = await axios.get(presignedUrl, { responseType: 'blob' });
          const objectURL = URL.createObjectURL(imageResponse.data);
          urls[photoPath] = objectURL;
        } catch (err) {
          console.error(`Failed to load image ${photoPath}`, err);
          urls[photoPath] = '/image-placeholder.png'; // Fallback image
        }
      });

      await Promise.all(promises);
      setLocalImageUrls(urls);
    };

    fetchAndCacheImages();

    // Cleanup object URLs on component unmount
    return () => {
      Object.values(localImageUrls).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [defectReport?.data?.photosWithDefects, defectReport?.data?.repeatedFindingsMap, assetDisplayImage]);


  // Update when all images have loaded
  useEffect(() => {
    if (totalImages > 0 && imagesLoaded >= totalImages) {
      setAllImagesLoaded(true);
    }
  }, [imagesLoaded, totalImages]);

  // Notify parent when report is ready (all images loaded or no images to load)
  const readyFiredRef = useRef(false);
  useEffect(() => {
    if (!onReady || readyFiredRef.current) return;
    if (allImagesLoaded) {
      readyFiredRef.current = true;
      onReady();
    } else if (totalImages === 0 && defectReport?.data) {
      // No images to load — ready after a brief DOM render delay
      const timer = setTimeout(() => {
        if (!readyFiredRef.current) {
          readyFiredRef.current = true;
          onReady();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [allImagesLoaded, totalImages, defectReport?.data, onReady]);

  // Handle image load event
  const handleImageLoad = () => {
    setImagesLoaded(prev => prev + 1);
  };

  // Expose generatePDF function to parent component
  useEffect(() => {
    if (onDownloadRef) {
      onDownloadRef.current = { generatePDF, handleDownload };
    }
  }, [onDownloadRef]);

  // Notify parent component when loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(generating);
    }
  }, [generating, onLoadingChange]);

  // Notify parent component when PDF progress changes
  useEffect(() => {
    if (onProgressChange) {
      onProgressChange(pdfProgress);
    }
  }, [pdfProgress, onProgressChange]);

  // Add global CSS to override Leaflet container background for PDF capture
  // The MUI sx prop doesn't work reliably with html2canvas DOM cloning
  useEffect(() => {
    const styleId = 'pdf-leaflet-override';

    // Remove existing style if any
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    // Create and inject style
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .leaflet-container {
        background: transparent !important;
        background-color: transparent !important;
      }
      .leaflet-tile-pane {
        opacity: 1 !important;
      }
      .leaflet-tile {
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleEl = document.getElementById(styleId);
      if (styleEl) styleEl.remove();
    };
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Road reports can be built from unreviewed AI findings instead of confirmed
  // defects. Only that path sets reportSummary.validationStatus, so every other
  // caller keeps the existing "defect" wording.
  const isPendingFindingsReport = reportSummary?.validationStatus === 'draft';
  const recordNoun = isPendingFindingsReport ? 'findings' : 'defects';
  const recordNounTitle = isPendingFindingsReport ? 'Findings' : 'Defects';

  const formatVehicleScope = (vehicleScope) => {
    if (!vehicleScope) return '';

    if (vehicleScope.mode === 'all') {
      return vehicleScope.totalRegisteredVehicles > 0
        ? `All vehicles (${vehicleScope.totalRegisteredVehicles} registered)`
        : 'All vehicles';
    }

    const vehicles = vehicleScope.vehicles || [];
    if (vehicles.length === 0) return 'Selected vehicles unavailable';

    if (vehicles.length === 1) {
      const vehicle = vehicles[0];
      return `${vehicle.deviceName || 'Vehicle'} (${vehicle.vehicleId || 'N/A'})`;
    }

    return `${vehicles.length} selected - ${vehicles.map(vehicle => vehicle.vehicleId || 'N/A').join(', ')}`;
  };

  // Format photo caption by removing the "Inspection image -" prefix
  const formatCaption = (caption) => {
    if (!caption) return '';
    return caption.replace(/^Inspection image\s*-\s*/i, '');
  };

  // Helper function to get severity name from ID
  const getSeverityName = (severityId, levels) => {
    if (severityId === null || severityId === undefined || !Array.isArray(levels)) {
      return 'Not specified';
    }
    const level = levels.find(l => l.id === severityId);
    return level ? level.name : `Unknown ID (${severityId})`;
  };

  // Helper function to build location string from available defect data
  // Uses the same format as DefectsPage.js to include bound information
  const getDefectLocation = (defect) => {
    // Use the hierarchical format: Network • Section • Chainage with Bound
    const locationParts = [
      defect.network_name,        // e.g., "NSE" 
      defect.section_name || defect.matched_section_name,  // e.g., "C3"
      defect.chainage_segment_name // e.g., "SB/KM442.15" (includes bound)
    ].filter(Boolean);
    
    // If we have the hierarchical data, use it with bullet separators like DefectsPage
    if (locationParts.length > 0) {
      return locationParts.join(' • ');
    }
    
    // Fallback: Try to construct manually if chainage_segment_name is missing
    const fallbackParts = [];
    
    if (defect.network_name) {
      fallbackParts.push(defect.network_name);
    }
    
    if (defect.section_name || defect.matched_section_name) {
      const sectionName = defect.section_name || defect.matched_section_name;
      fallbackParts.push(sectionName);
    }
    
    // Add chainage/km information with direction if available
    let chainagePart = '';
    if (defect.km_marker !== null && defect.km_marker !== undefined) {
      chainagePart = `KM ${defect.km_marker}`;
    } else if (defect.chainage_marker) {
      chainagePart = defect.chainage_marker;
    }
    
    // Add direction/bound to chainage if available
    if (chainagePart && defect.direction) {
      chainagePart = `${defect.direction}/${chainagePart}`;
    }
    
    if (chainagePart) {
      fallbackParts.push(chainagePart);
    }
    
    if (fallbackParts.length > 0) {
      return fallbackParts.join(' • ');
    }
    
    // Final fallback: GPS coordinates
    if (defect.latitude && defect.longitude) {
      return `GPS: ${defect.latitude.toFixed(6)}, ${defect.longitude.toFixed(6)}`;
    }
    
    return 'Location not specified';
  };

  // Handle PDF generation (returns PDF data for upload or triggers download)
  const generatePDF = async (shouldDownload = true) => {
    if (!reportRef.current) {
      setError('Report container not found.');
      return null;
    }
    
    const pageElements = reportRef.current.querySelectorAll('.pdf-page-element');
    if (pageElements.length === 0) {
      setError('No report pages found to generate PDF.');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setPdfProgress({ current: 0, total: pageElements.length, phase: 'Loading map tiles...' });

      // Fixed delay to ensure map tiles are loaded
      // (Event-based tracking doesn't work because html2canvas clones the DOM and triggers re-renders)
      await new Promise(resolve => setTimeout(resolve, 3000));

      setPdfProgress({ current: 0, total: pageElements.length, phase: 'Rendering pages...' });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfPageWidth = 210; // A4 width in mm
      const pdfPageHeight = 297; // A4 height in mm

      for (let i = 0; i < pageElements.length; i++) {
        setPdfProgress({ current: i + 1, total: pageElements.length, phase: 'Rendering' });
        const pageElement = pageElements[i];

        // Use html2canvas with onclone callback to fix Leaflet backgrounds in cloned DOM
        const canvas = await html2canvas(pageElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false, // Set to true for verbose html2canvas logging
          onclone: (clonedDoc) => {
            // Ensure Leaflet containers in cloned DOM have transparent background
            const containers = clonedDoc.querySelectorAll('.leaflet-container');
            containers.forEach(container => {
              container.style.background = 'transparent';
              container.style.backgroundColor = 'transparent';
            });
          }
        });

        // Use JPEG with high quality (PNG creates files too large for upload)
        const imgData = canvas.toDataURL('image/jpeg', 0.92);

        if (i > 0) {
          pdf.addPage();
        }

        // Simple image addition
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfPageWidth, pdfPageHeight);

        // Add internal hyperlinks on the Defect Location Plan page for each marker
        // The display image page is at index 2 (Cover=0, Summary=1, DisplayImage=2) when present
        if (hasDisplayImagePage && i === 2 && Object.keys(defectMarkers).length > 0) {
          const pageRect = pageElement.getBoundingClientRect();
          const pageW = pageRect.width;
          const pageH = pageRect.height;

          Object.entries(defectMarkers).forEach(([defectId, pos]) => {
            const defectIndex = (defectReport?.data?.defects || []).findIndex(d => String(d.id) === String(defectId));
            if (defectIndex === -1) return;

            // Calculate which PDF page this defect lands on
            // Pages before defect details: Cover(1) + Summary(1) + DisplayImage(1) + PhotoPages + Breaker(1)
            let priorRP = 0;
            for (let di = 0; di < defectIndex; di++) {
              priorRP += (repeatedPagesByDefect[di] || 0);
            }
            const targetPage = 1 + 1 + 1 + numPhotoPages + 1 + (defectIndex + 1) + priorRP;

            // The marker's position is a % of the image container.
            // Find the image container within this page element to get its bounds.
            const imgContainer = pageElement.querySelector('[data-display-image-container]');
            if (imgContainer) {
              const containerRect = imgContainer.getBoundingClientRect();
              // Marker absolute pixel position within the page
              const markerPxX = (containerRect.left - pageRect.left) + (pos.x / 100) * containerRect.width;
              const markerPxY = (containerRect.top - pageRect.top) + (pos.y / 100) * containerRect.height;
              // Convert to PDF mm
              const linkW = 8; // mm - clickable area width
              const linkH = 10; // mm - clickable area height
              const pdfX = (markerPxX / pageW) * pdfPageWidth - linkW / 2;
              const pdfY = (markerPxY / pageH) * pdfPageHeight - linkH;
              pdf.link(pdfX, pdfY, linkW, linkH, { pageNumber: targetPage });
            }
          });
        }
      }
      
      if (shouldDownload) {
        pdf.save(`inspection-report-${inspection?.id || 'unknown'}.pdf`);
      }
      
      // Return PDF data for upload purposes
      return {
        pdfBlob: pdf.output('blob'),
        filename: `inspection-report-${inspection?.id || 'unknown'}.pdf`
      };
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
      return null;
    } finally {
      setGenerating(false);
      setPdfProgress({ current: 0, total: 0, phase: '' });
    }
  };

  // Wrapper for backward compatibility
  const handleDownload = () => generatePDF(true);

  // Download PDF and save to database
  const handleDownloadAndSave = async () => {
    if (!inspection || !assetType) {
      setError('Missing inspection or asset type information');
      return;
    }

    try {
      setSavingToDb(true);
      setSaveError(null);
      
      // Generate PDF without auto-download
      const pdfData = await generatePDF(false);
      if (!pdfData) {
        throw new Error('Failed to generate PDF');
      }

      // Prepare report metadata
      const reportName = `${reportTitle} - ${new Date().toLocaleDateString()}`;
      const description = `${assetType} inspection report containing ${defectReport?.data?.total || 0} defects`;
      
      // Build report parameters
      const parameters = {
        inspectionId: inspection.id,
        assetType: assetType,
        generatedAt: new Date().toISOString(),
        defectCount: defectReport?.data?.total || 0,
        photoCount: defectReport?.data?.photosWithDefectsCount || 0
      };

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('pdfFile', pdfData.pdfBlob, pdfData.filename);
      formData.append('reportName', reportName);
      formData.append('reportType', 'inspection_report');
      formData.append('assetType', assetType);
      formData.append('description', description);
      formData.append('parameters', JSON.stringify(parameters));
      if (inspection.asset_id) {
        formData.append('assetId', inspection.asset_id);
      }
      
      if (inspection.inspection_date) {
        formData.append('startDate', new Date(inspection.inspection_date).toISOString().split('T')[0]);
        formData.append('endDate', new Date(inspection.inspection_date).toISOString().split('T')[0]);
      }

      // Upload to backend
      setPdfProgress({ current: 0, total: 100, phase: 'Uploading' });
      const response = await api.post('/reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setPdfProgress({ current: percent, total: 100, phase: 'Uploading' });
        }
      });

      if (response.data.success) {
        setSaveSuccess(true);
        
        // Trigger download for user
        const url = URL.createObjectURL(pdfData.pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdfData.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSaveSuccess(false);
        }, 5000);
      } else {
        throw new Error(response.data.error || 'Failed to save report');
      }

    } catch (error) {
      console.error('Error downloading and saving report:', error);
      setSaveError(error.message || 'Failed to save report to database');
      
      // Fallback: try to download anyway
      try {
        await generatePDF(true);
      } catch (downloadError) {
        console.error('Fallback download also failed:', downloadError);
      }
    } finally {
      setSavingToDb(false);
    }
  };

  if (!inspection) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>No inspection data available.</Typography>
      </Box>
    );
  }

  // Calculate the number of photo pages
  const numPhotoPages = defectReport?.data?.photosWithDefects
    ? Math.ceil(defectReport.data.photosWithDefects.length / PHOTOS_PER_PAGE)
    : 0;

  // Calculate the number of defect detail pages
  const numDefectDetailPages = defectReport?.data?.defects?.length || 0;

  // Calculate repeated findings pages per defect
  const repeatedFindingsMap = defectReport?.data?.repeatedFindingsMap || {};
  const defectsForPages = defectReport?.data?.defects || [];
  const repeatedPagesByDefect = {};
  let numRepeatedFindingsPages = 0;
  defectsForPages.forEach((defect, idx) => {
    const defectId = defect.defect_id || defect.id;
    const findings = repeatedFindingsMap[defectId] || [];
    const pages = Math.ceil(findings.length / 2);
    repeatedPagesByDefect[idx] = pages;
    numRepeatedFindingsPages += pages;
  });

  // Whether we have an asset display image page
  const hasDisplayImagePage = !!assetDisplayImage;

  // Normalize the display image path for lookup in localImageUrls
  const displayImagePath = assetDisplayImage
    ? (() => { const p = assetDisplayImage.replace(/\\/g, '/'); return p.startsWith('/') ? p.slice(1) : p; })()
    : null;

  // Calculate final total pages
  // Cover (1) + Summary (1) + Display Image (0 or 1) + Photo Pages + Breaker (1) + Defect Detail Pages + Repeated Finding Pages + Back Cover (1)
  const finalTotalPages = 1 + 1 + (hasDisplayImagePage ? 1 : 0) + numPhotoPages + 1 + numDefectDetailPages + numRepeatedFindingsPages + 1;

  // Defect number lookup: defectId → report number (1-based), matching the # in Detailed Findings
  const defectNumberMap = {};
  (defectReport?.data?.defects || []).forEach((d, i) => {
    defectNumberMap[String(d.id)] = i + 1;
  });

  // --- Defect marking handlers ---
  const handleDisplayImageClick = (e) => {
    if (!markingMode || !defectReport?.data?.defects?.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setDefectPickerAnchor({ mouseX: e.clientX, mouseY: e.clientY, x, y });
  };

  const handleDefectMarkerSelect = (defectId) => {
    if (!defectPickerAnchor) return;
    setDefectMarkers(prev => ({
      ...prev,
      [String(defectId)]: { x: defectPickerAnchor.x, y: defectPickerAnchor.y }
    }));
    setDefectPickerAnchor(null);
  };

  const handleRemoveMarker = (defectId, e) => {
    e.stopPropagation();
    setDefectMarkers(prev => {
      const next = { ...prev };
      delete next[String(defectId)];
      return next;
    });
  };
  // --- end defect marking handlers ---

  return (
    <Box sx={
      fullPage
        ? {
            // Full-page mode: PLAIN BLOCK layout (no flex).
            // Critical: position:sticky on a child of a flex container with flex:1 sibling does NOT
            // work correctly because the flex item's box stays at its allocated size while content
            // overflows; the sticky child's natural position then scrolls with the parent instead
            // of sticking. Block layout puts the sticky bar at the natural end of the content flow,
            // which is what sticky needs.
            minHeight: '100%',
            width: '100%',
          }
        : {
            // Modal mode (default): fixed-height column with internal scroll on the inner Box.
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }
    }>
      {/* Pages container */}
      <Box sx={
        fullPage
          ? {
              // No internal scroll, no flex - just a natural-height block.
              // The parent (MainContainer through PDFReportPage) handles scrolling.
              bgcolor: '#f5f5f5',
              p: 2,
            }
          : {
              flex: 1,
              overflow: 'auto',
              bgcolor: '#f5f5f5',
              p: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
            }
      }>
        {/* Container for all pages */}
        <Box 
          ref={reportRef}
          sx={{
            width: '100%',
            maxWidth: '760px',
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4, // Space between pages
          }}
        >
          {/* Page 1: Cover Page */}
          <Paper 
            className="pdf-page-element"
            sx={{
            width: '100%',
            aspectRatio: '210/297',
            bgcolor: '#ffffff',
            boxShadow: 3,
            border: '1px solid #ddd',
            overflow: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative', // For page number absolute positioning
          }}>
            {/* Cover Page Content */}
            <Box sx={{ 
              p: 4,
              flex: 1,
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <Typography
                variant="h3"
                sx={{
                  color: '#003366',
                  fontWeight: 600,
                  mb: 4,
                  textAlign: 'center',
                  // Slightly smaller title for Vertical Asset ("High Mast Inspection Report" is longer)
                  fontSize: reportSummary?.isVerticalAsset ? '2.3rem' : undefined,
                }}
              >
                {reportTitle}
              </Typography>
              
              {/* Report Summary Section */}
              {reportSummary && (
                <Box sx={{ 
                  width: '100%', 
                  maxWidth: '500px', 
                  mb: 4,
                  p: 3,
                  border: '2px solid #003366',
                  borderRadius: 2,
                  bgcolor: 'rgba(0, 51, 102, 0.02)'
                }}>
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600, mb: 2, textAlign: 'center' }}>
                    Report Summary
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Network */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                        Network:
                      </Typography>
                      <Typography variant="body2">
                        {reportSummary.network?.name || 'All Networks'}
                      </Typography>
                    </Box>

                    {/* Sections or Custom Field (e.g. Asset ID) */}
                    {reportSummary.customField ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          {reportSummary.customField.label}:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.customField.value}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Sections:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.sectionsMode === 'all'
                            ? 'All sections'
                            : reportSummary.selectedSections?.length > 0
                              ? `${reportSummary.selectedSections.length} specific sections`
                              : 'Whole network (no sections defined)'
                          }
                        </Typography>
                      </Box>
                    )}

                    {/* Vehicle Sources (road reports only) */}
                    {reportSummary.vehicleScope && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Vehicles:
                        </Typography>
                        <Typography variant="body2">
                          {formatVehicleScope(reportSummary.vehicleScope)}
                        </Typography>
                      </Box>
                    )}

                    {/* Asset Rating - from asset's rating_level_id (convert Cx to Rx for display) */}
                    {reportSummary.assetRating && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Asset Rating:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.assetRating.replace('Cx', 'Rx')}
                        </Typography>
                      </Box>
                    )}

                    {/* Inspection Type - Optional */}
                    {reportSummary.inspectionType && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Inspection Type:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.inspectionType}
                        </Typography>
                      </Box>
                    )}

                    {/* Inspection Period (Cycle + Year) */}
                    {reportSummary.inspectionCycle && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Inspection Period:
                        </Typography>
                        <Typography variant="body2">
                          Cycle {reportSummary.inspectionCycle} {reportSummary.startDate ? reportSummary.startDate.getFullYear() : new Date().getFullYear()}
                        </Typography>
                      </Box>
                    )}

                    {/* Condition Rating - from inspection's condition_rating_level_id (Cx format). Hidden for Vertical Assets. */}
                    {!reportSummary.isVerticalAsset && (reportSummary.inspectionConditionRating || reportSummary.inspectionRating || reportSummary.conditionRating) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Condition Rating:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.inspectionConditionRating || reportSummary.inspectionRating || reportSummary.conditionRating}
                        </Typography>
                      </Box>
                    )}

                    {/* Rating remark — inspector remark, falling back to the rating level's standard description */}
                    {!reportSummary.isVerticalAsset && reportSummary.inspectionRemarks && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Remarks:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.inspectionRemarks}
                        </Typography>
                      </Box>
                    )}

                    {/* Slanting tilt (Vertical Asset only) — latest completed Slanting Analysis, if any */}
                    {reportSummary.isVerticalAsset && reportSummary.slantingDegrees != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Slanting:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.slantingDegrees.toFixed(2)}°{reportSummary.slantingLabel ? ` — ${reportSummary.slantingLabel}` : ''}
                        </Typography>
                      </Box>
                    )}

                    {/* Date Range */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                        Date Range:
                      </Typography>
                      <Typography variant="body2">
                        {reportSummary.startDate && reportSummary.endDate
                          ? `${reportSummary.startDate.toLocaleDateString()} - ${reportSummary.endDate.toLocaleDateString()}`
                          : 'N/A'
                        }
                      </Typography>
                    </Box>

                    {/* Defect Types */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                        Defect Types:
                      </Typography>
                      <Typography variant="body2">
                        {reportSummary.selectAllDefects
                          ? `All types (${reportSummary.totalDefectTypes})`
                          : `${reportSummary.selectedDefectTypes?.length || 0} selected types`
                        }
                      </Typography>
                    </Box>

                    {/* Report content - only set for road reports */}
                    {reportSummary.validationStatus && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Content:
                        </Typography>
                        <Typography variant="body2">
                          {isPendingFindingsReport
                            ? 'AI findings pending validation - types and locations unconfirmed'
                            : 'Validated defects'}
                        </Typography>
                      </Box>
                    )}

                    {/* Total Defects */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                        Total {recordNounTitle}:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#003366', fontWeight: 600 }}>
                        {defectReport?.data ? (defectReport.data.total ||
                          (Array.isArray(defectReport.data.defects) ? defectReport.data.defects.length : 0)) : 0} {recordNoun} found
                      </Typography>
                    </Box>

                    {/* Repeated Findings */}
                    {reportSummary.includeRepeatedFindings && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '130px' }}>
                          Repeated Findings:
                        </Typography>
                        <Typography variant="body2">
                          {reportSummary.onlyDefectsWithRepeated
                            ? 'Included — only defects with repeated detections'
                            : 'Included (all-time detections)'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              {reportSummary?.generatedBy && (
                <Typography variant="body1" sx={{ mb: 1, textAlign: 'center', fontStyle: 'italic' }}>
                  Report generated by: {reportSummary.generatedBy}
                </Typography>
              )}
            </Box>

            {/* Right side stripes */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: '30px',
                bgcolor: '#4CAF50', // Green
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: '35px', // 30px for green stripe + 5px gap
                bottom: 0,
                width: '30px',
                bgcolor: '#0D47A1', // Dark Blue
              }}
            />
          </Paper>

          {/* Page 2: Summary Page */}
          <Paper 
            className="pdf-page-element"
            sx={{
            width: '100%',
            aspectRatio: '210/297',
            bgcolor: '#ffffff',
            boxShadow: 3,
            border: '1px solid #ddd',
            overflow: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}>
            {/* Summary Page Content */}
            <Box sx={{ 
              p: 4,
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#ccc',
                borderRadius: '4px',
              },
            }}>
              {/* Report Header */}
              <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid #003366' }}>
                <Typography variant="h4" sx={{ color: '#003366', fontWeight: 600, mb: 1 }}>
                  {reportTitle}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Date Range: {reportSummary?.startDate && reportSummary?.endDate
                    ? `${reportSummary.startDate.toLocaleDateString()} - ${reportSummary.endDate.toLocaleDateString()}`
                    : formatDate(inspection?.inspection_date || inspection?.date)}
                </Typography>
                {inspection?.cycle && (
                  <Typography variant="body1">
                    Cycle: {inspection.cycle} {reportSummary?.startDate ? reportSummary.startDate.getFullYear() : new Date().getFullYear()}
                  </Typography>
                )}
              </Box>

              {/* Summary Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, color: '#003366', fontWeight: 600 }}>
                  {recordNounTitle === 'Defects' ? 'Defect Summary' : 'Finding Summary'}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1">
                        <strong>Total {recordNounTitle}:</strong> {' '}
                        {defectReport?.data ? (defectReport.data.total ||
                          (Array.isArray(defectReport.data.defects) ? defectReport.data.defects.length : 0)) : 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1">
                        <strong>Images with {recordNounTitle}:</strong> {' '}
                        {defectReport?.data?.photosWithDefectsCount || 0}
                      </Typography>
                    </Grid>
                    {reportSummary?.vehicleScope && (
                      <Grid item xs={12}>
                        <Typography variant="body1">
                          <strong>Vehicles:</strong> {' '}
                          {formatVehicleScope(reportSummary.vehicleScope)}
                        </Typography>
                      </Grid>
                    )}
                    {isPendingFindingsReport && (
                      <Grid item xs={12}>
                        <Typography variant="body1" sx={{ color: '#8a5000' }}>
                          <strong>Note:</strong> {' '}
                          These findings are AI detections that have not been validated.
                          Defect types and locations are unconfirmed.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  
                  {defectReport?.data?.defectTypes && defectReport.data.defectTypes.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Defect Types:</strong>
                      </Typography>
                      <Grid container spacing={1}>
                        {defectReport.data.defectTypes.map((type, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Typography variant="body2">
                              • {type.defect_type_name || 'Unknown'}: {type.count}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* Defect Locations Overview Map */}
              {defectReport?.data?.defects && defectReport.data.defects.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" sx={{ mb: 2, color: '#003366', fontWeight: 600 }}>
                    Defect Locations Overview
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 0, bgcolor: 'transparent', overflow: 'hidden' }}>
                    <AllDefectsOverviewMap
                      defects={defectReport.data.defects}
                      photos={defectReport.data.photosWithDefects}
                    />
                  </Paper>
                </Box>
              )}
            </Box>
            
            {/* Footer */}
            <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #ddd' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Page 2 of {finalTotalPages}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Page 3 (conditional): Asset Display Image Page */}
          {hasDisplayImagePage && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Defect marking toolbar — sits between pages, NOT inside pdf-page-element, never captured */}
              {defectReport?.data?.defects?.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  boxShadow: 1,
                }}>
                  <Button
                    size="small"
                    variant={markingMode ? 'contained' : 'outlined'}
                    color={markingMode ? 'error' : 'primary'}
                    onClick={() => { setMarkingMode(m => !m); setDefectPickerAnchor(null); }}
                    sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                  >
                    {markingMode ? 'Done Marking' : 'Mark Defects on Image'}
                  </Button>
                  {Object.keys(defectMarkers).length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      onClick={() => setDefectMarkers({})}
                      sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary' }}
                    >
                      Clear All
                    </Button>
                  )}
                  {markingMode && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Click image to mark &bull; Click marker to remove
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.8rem' }}>
                    {Object.keys(defectMarkers).length} / {defectReport.data.defects.length} marked
                  </Typography>
                </Box>
              )}

              <Paper
                className="pdf-page-element"
                sx={{
                  width: '100%',
                  aspectRatio: '210/297',
                  bgcolor: '#ffffff',
                  boxShadow: 3,
                  border: '1px solid #ddd',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}>
                {/* Display Image Page Content */}
                <Box sx={{
                  p: 4,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {/* Header */}
                  <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid #003366' }}>
                    <Typography variant="h4" sx={{ color: '#003366', fontWeight: 600, mb: 1 }}>
                      Defect Location Plan
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {reportSummary?.customField?.value ? `Asset ID: ${reportSummary.customField.value}` : ''}
                      {reportSummary?.network?.name && reportSummary.network.name !== 'N/A' ? ` — ${reportSummary.network.name}` : ''}
                    </Typography>
                  </Box>

                  {/* Display Image with defect location markers */}
                  <Box
                    data-display-image-container
                    sx={{
                      flex: 1,
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                      borderRadius: 1,
                      border: markingMode ? '2px dashed #d32f2f' : '1px solid #e0e0e0',
                      bgcolor: '#f5f5f5',
                      cursor: markingMode ? 'crosshair' : 'default',
                      transition: 'border 0.2s ease',
                    }}
                    onClick={markingMode ? handleDisplayImageClick : undefined}
                  >
                    <img
                      src={localImageUrls[displayImagePath] || '/image-placeholder.png'}
                      alt="Asset Display"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        display: 'block',
                      }}
                      onLoad={handleImageLoad}
                      onError={(e) => {
                        e.target.src = '/image-placeholder.png';
                      }}
                    />

                    {/* Defect location markers — always rendered (captured in PDF) */}
                    {Object.entries(defectMarkers).map(([defectId, pos]) => {
                      const num = defectNumberMap[String(defectId)];
                      const defect = (defectReport?.data?.defects || []).find(d => String(d.id) === String(defectId));
                      return (
                        <Box
                          key={defectId}
                          title={`#${num} — ${defect?.defect_type_name || 'Defect'}${markingMode ? ' (click to remove)' : ''}`}
                          sx={{
                            position: 'absolute',
                            left: `${pos.x}%`,
                            top: `${pos.y}%`,
                            transform: 'translate(-50%, -100%)',
                            zIndex: 5,
                            cursor: markingMode ? 'pointer' : 'default',
                            '&:hover': markingMode
                              ? { transform: 'translate(-50%, -100%) scale(1.2)', filter: 'brightness(1.1)' }
                              : {},
                            pointerEvents: 'auto',
                          }}
                          onClick={markingMode ? (e) => handleRemoveMarker(defectId, e) : (e) => e.stopPropagation()}
                        >
                          {/* Pin circle with defect number */}
                          <Box sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            bgcolor: '#d32f2f',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            border: '2px solid #fff',
                            userSelect: 'none',
                          }}>
                            {num}
                          </Box>
                          {/* Pin pointer triangle */}
                          <Box sx={{
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '8px solid #d32f2f',
                            mx: 'auto',
                            mt: '-2px',
                          }} />
                        </Box>
                      );
                    })}

                    {/* Marking mode hint — excluded from PDF */}
                    {markingMode && Object.keys(defectMarkers).length === 0 && (
                      <Box
                        data-html2canvas-ignore="true"
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          px: 2,
                          py: 0.75,
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          pointerEvents: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Click on the image to mark a defect location
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #ddd' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Page 3 of {finalTotalPages}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Defect picker menu — renders in a portal, never captured in PDF */}
              <Menu
                open={!!defectPickerAnchor}
                onClose={() => setDefectPickerAnchor(null)}
                anchorReference="anchorPosition"
                anchorPosition={defectPickerAnchor ? { top: defectPickerAnchor.mouseY, left: defectPickerAnchor.mouseX } : undefined}
                slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 240 } } }}
              >
                <MenuItem disabled sx={{ opacity: '1 !important', minHeight: 'auto', py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Select defect to place:
                  </Typography>
                </MenuItem>
                {(defectReport?.data?.defects || []).map((defect, index) => {
                  const isPlaced = defectMarkers.hasOwnProperty(String(defect.id));

                  // Resolve the defect's photo URL from the pre-loaded cache
                  const rawPath = defect.photo_path || defect.original_image_path || '';
                  const normPath = rawPath.replace(/\\/g, '/').replace(/^\//, '');
                  const defectImgUrl = localImageUrls[normPath] || null;

                  // Bounding-box percentages (relative to image true dimensions)
                  const bbox = defect.bounding_box;
                  const ow = defect.image_width || 1;
                  const oh = defect.image_height || 1;
                  const hasBbox = bbox && bbox.x1 != null && bbox.y1 != null && bbox.x2 != null && bbox.y2 != null;

                  const tooltipContent = (
                    <Box sx={{ width: 240, p: 0.5 }}>
                      {/* Photo with bounding-box overlay */}
                      {defectImgUrl && (
                        <Box sx={{ position: 'relative', width: '100%', mb: 1, borderRadius: 0.5, overflow: 'hidden', lineHeight: 0 }}>
                          <img src={defectImgUrl} alt="" style={{ width: '100%', display: 'block', borderRadius: 4 }} />
                          {hasBbox && (
                            <Box sx={{
                              position: 'absolute',
                              left: `${(parseFloat(bbox.x1) / ow) * 100}%`,
                              top: `${(parseFloat(bbox.y1) / oh) * 100}%`,
                              width: `${((parseFloat(bbox.x2) - parseFloat(bbox.x1)) / ow) * 100}%`,
                              height: `${((parseFloat(bbox.y2) - parseFloat(bbox.y1)) / oh) * 100}%`,
                              border: '2px solid #ff1744',
                              boxSizing: 'border-box',
                              pointerEvents: 'none',
                            }} />
                          )}
                        </Box>
                      )}
                      {/* Defect details */}
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#fff', mb: 0.25 }}>
                        #{index + 1} — {defect.defect_type_name || 'Unknown'}
                      </Typography>
                      {defect.severity_level_name && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.8)' }}>
                          Severity: {defect.severity_level_name}
                        </Typography>
                      )}
                      {defect.status && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.8)' }}>
                          Status: {defect.status}
                        </Typography>
                      )}
                      {defect.description && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', mt: 0.5, fontStyle: 'italic' }} noWrap>
                          {defect.description}
                        </Typography>
                      )}
                    </Box>
                  );

                  return (
                    <Tooltip
                      key={defect.id}
                      title={tooltipContent}
                      placement="right"
                      arrow
                      enterDelay={300}
                      enterNextDelay={150}
                      slotProps={{
                        tooltip: { sx: { maxWidth: 260, bgcolor: 'rgba(33,33,33,0.96)', p: 1, borderRadius: 1.5 } },
                        arrow: { sx: { color: 'rgba(33,33,33,0.96)' } },
                      }}
                    >
                      {/* span wrapper ensures Tooltip works on disabled MenuItems */}
                      <span>
                        <MenuItem
                          disabled={isPlaced}
                          onClick={() => handleDefectMarkerSelect(defect.id)}
                          sx={{ py: 0.75 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Box sx={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              bgcolor: isPlaced ? '#bdbdbd' : '#d32f2f',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}>
                              {index + 1}
                            </Box>
                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                              {defect.defect_type_name || 'Unknown Defect'}
                            </Typography>
                            {isPlaced && (
                              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>✓</Typography>
                            )}
                          </Box>
                        </MenuItem>
                      </span>
                    </Tooltip>
                  );
                })}
              </Menu>
            </Box>
          )}

          {/* Photo Pages - Modified to support pagination */}
          {defectReport?.data?.photosWithDefects && defectReport.data.photosWithDefects.length > 0 && (
            <>
              {/* Calculate how many photo pages we need */}
              {Array.from({ length: numPhotoPages }).map((_, pageIndex) => {
                // Calculate current page number for photo pages
                // Cover (1) + Summary (1) + Display Image (0 or 1) + current photo page index (0-based)
                const currentPageNumber = 1 + 1 + (hasDisplayImagePage ? 1 : 0) + (pageIndex + 1);
                
                return (
                  <Paper 
                    key={`photo-page-${pageIndex}`}
                    className="pdf-page-element"
                    sx={{
                    width: '100%',
                    aspectRatio: '210/297',
                    bgcolor: '#ffffff',
                    boxShadow: 3,
                    border: '1px solid #ddd',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                  }}>
                    {/* Photos Page Content */}
                    <Box sx={{ 
                      p: 4,
                      flex: 1,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#ccc',
                        borderRadius: '4px',
                      },
                    }}>
                      <Typography variant="h5" sx={{ mb: 3, color: '#003366', fontWeight: 600 }}>
                        Photos with Defects
                      </Typography>
                      
                      {/* Perfectly uniform grid layout with 4 rows */}
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1.5, // Slightly reduced gap to fit more rows
                        width: '100%'
                      }}>
                        {defectReport.data.photosWithDefects
                          .slice(pageIndex * PHOTOS_PER_PAGE, (pageIndex + 1) * PHOTOS_PER_PAGE)
                          .map((photo, index) => {
                            // Get photo URL with better path handling
                            let photoUrl = null;
                            
                            // Attempt multiple sources in order of preference
                            if (photo.url) {
                              photoUrl = photo.url;
                            } else if (photo.photo_path) {
                              photoUrl = photo.photo_path;
                            } else if (photo.path) {
                              photoUrl = photo.path;
                            }
                            
                            // New photo filtering logic based on defect.photo_path
                            const defectPhotos = [];
                            if (photo.photo_path) {
                              const normalizedDefectPhotoPath = photo.photo_path.replace(/\\/g, '/');
                              const relatedPhoto = defectReport?.data?.photosWithDefects?.find(p => {
                                const normalizedPhotoObjectPath = p.photo_path?.replace(/\\/g, '/');
                                return normalizedPhotoObjectPath === normalizedDefectPhotoPath;
                              });
                              if (relatedPhoto) {
                                defectPhotos.push(relatedPhoto);
                              }
                            }

                            // Only proceed with photos that are directly related to this defect
                            const hasPhotos = defectPhotos.length > 0;

                            return (
                              <Box 
                                key={`${pageIndex}-${index}`} 
                                sx={{
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  bgcolor: 'white',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  height: '100%',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}
                              >
                                {/* Fixed aspect ratio container for image */}
                                <Box sx={{ 
                                  paddingTop: '56.25%',
                                  position: 'relative',
                                  width: '100%',
                                  bgcolor: '#f0f0f0'
                                }}>
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0
                                  }}>
                                    <img
                                      src={localImageUrls[photoUrl] || '/image-placeholder.png'}
                                      alt={formatCaption(photo.caption) || `Photo ${index + 1 + (pageIndex * PHOTOS_PER_PAGE)}`}
                                      style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                                      onLoad={handleImageLoad}
                                      onError={(e) => {
                                        e.target.src = '/image-placeholder.png';
                                        handleImageLoad();
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {/* Caption area with reduced height */}
                                <Box sx={{ 
                                  p: 1, 
                                  flexGrow: 0,
                                  height: '36px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center'
                                }}>
                                  <Typography 
                                    variant="body2" 
                                    noWrap 
                                    fontSize="0.7rem" 
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {formatCaption(photo.caption) || `Photo ${index + 1 + (pageIndex * PHOTOS_PER_PAGE)}`}
                                  </Typography>
                                  {photo.annotation_count > 0 && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      fontSize="0.65rem"
                                    >
                                      {photo.annotation_count} annotation{photo.annotation_count > 1 ? 's' : ''}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                    
                    {/* Footer */}
                    <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #ddd' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Page {currentPageNumber} of {finalTotalPages}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </>
          )}

          {/* Breaker page: Detailed Defect Findings */}
          <Paper 
            className="pdf-page-element"
            sx={{
              width: '100%',
              aspectRatio: '210/297',
              bgcolor: '#ffffff',
              boxShadow: 3,
              border: '1px solid #ddd',
              overflow: 'hidden',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}>
            {/* Breaker Page Content */}
            <Box sx={{ 
              p: 4,
              flex: 1,
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 600, mb: 4, textAlign: 'center' }}>
                Detailed Defect Findings
              </Typography>
              
              <Box sx={{ 
                width: '120px', 
                height: '120px', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                mb: 4
              }}>
                <ErrorIcon sx={{ fontSize: 64, color: '#003366' }} />
              </Box>
              
              <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
                The following pages contain detailed information about each defect identified during the inspection.
              </Typography>
              
              <Divider sx={{ width: '50%', my: 4 }} />

              <Typography variant="body1" sx={{ textAlign: 'center' }}>
                {reportSummary?.inspectionType || inspection?.title || 'Untitled Inspection'}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                Date Range: {reportSummary?.startDate && reportSummary?.endDate
                  ? `${reportSummary.startDate.toLocaleDateString()} - ${reportSummary.endDate.toLocaleDateString()}`
                  : formatDate(inspection?.inspection_date || inspection?.date)}
              </Typography>
            </Box>
            
            {/* Footer */}
            <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #ddd' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {/* Breaker page is after Cover (1), Summary (1), Display Image (0 or 1), and all Photo pages */}
                  Page {1 + 1 + (hasDisplayImagePage ? 1 : 0) + numPhotoPages + 1} of {finalTotalPages}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Detailed defect pages - one page per defect, with optional repeated findings pages */}
          {defectReport?.data?.defects && defectReport.data.defects.map((defect, defectIndex) => {
            // Calculate cumulative repeated finding pages from prior defects
            let priorRepeatedPages = 0;
            for (let i = 0; i < defectIndex; i++) {
              priorRepeatedPages += (repeatedPagesByDefect[i] || 0);
            }
            // Cover (1) + Summary (1) + Display Image (0 or 1) + Photo Pages + Breaker (1) + prior defect pages + prior repeated pages + current (1-based)
            const currentPage = 1 + 1 + (hasDisplayImagePage ? 1 : 0) + numPhotoPages + 1 + (defectIndex + 1) + priorRepeatedPages;

            // Corrected photo filtering logic using defect.photo_id or original_image_path
            const defectPhotos = [];
            if (defect.photo_id) {
              const relatedPhoto = defectReport?.data?.photosWithDefects?.find(p => p.id === defect.photo_id);
              if (relatedPhoto) {
                defectPhotos.push(relatedPhoto);
              }
            } else if (defect.original_image_path) {
              const normalizedDefectPath = defect.original_image_path.replace(/\\/g, '/');
              const relatedPhoto = defectReport?.data?.photosWithDefects?.find(p => p.photo_path === normalizedDefectPath);
              if (relatedPhoto) {
                defectPhotos.push(relatedPhoto);
              }
            }

            // Only proceed with photos that are directly related to this defect
            const hasPhotos = defectPhotos.length > 0;

            // Get repeated findings for this defect
            const defectId = defect.defect_id || defect.id;
            const defectRepeatedFindings = repeatedFindingsMap[defectId] || [];
            const numRfPages = repeatedPagesByDefect[defectIndex] || 0;

            return (
              <React.Fragment key={`defect-group-${defectIndex}`}>
              <Paper
                key={`defect-${defectIndex}`}
                className="pdf-page-element"
                sx={{
                  width: '100%',
                  aspectRatio: '210/297',
                  bgcolor: '#ffffff',
                  boxShadow: 3,
                  border: '1px solid #ddd',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {/* Defect page content - grid: header (auto) | photos (1fr, absorbs leftover) | rest (auto, sized to square map) */}
                <Box sx={{
                  p: 4,
                  flex: 1,
                  display: 'grid',
                  gridTemplateRows: 'auto 1fr auto',
                  rowGap: 2,
                  minHeight: 0,
                  overflow: 'hidden',
                }}>
                  {/* Row 1: Defect header */}
                  <Box sx={{ minHeight: 0 }}>
                    <Typography variant="h5" sx={{ color: '#003366', fontWeight: 600 }}>
                      Defect #{defectIndex + 1}: {defect.defect_type_name || 'Unnamed Defect'}
                    </Typography>
                  </Box>

                  {/* Row 2: Defect photos - locked to half page via grid 1fr; single photo only */}
                  <Box sx={{
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, flexShrink: 0 }}>
                      Defect Photos:
                    </Typography>
                    {hasPhotos ? (() => {
                      // Single photo per defect detail page
                      const photo = defectPhotos[0];
                      let photoUrl = null;
                      if (photo.url) photoUrl = photo.url;
                      else if (photo.photo_path) photoUrl = photo.photo_path;
                      else if (photo.path) photoUrl = photo.path;

                      const ow = parseFloat(photo.image_width) || 0;
                      const oh = parseFloat(photo.image_height) || 0;
                      // Use the photo's true aspect ratio so the inner frame matches the image exactly,
                      // which keeps bbox % overlay coordinates aligned with the actual image bounds.
                      const photoAspectRatio = (ow > 0 && oh > 0) ? `${ow} / ${oh}` : '16 / 9';

                      return (
                        <Box sx={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          {/* Letterbox slot: fills the half-page row, centers any-orientation image */}
                          <Box sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            bgcolor: '#f5f5f5',
                          }}>
                            {/* Inner image frame: aspect-ratio matches the actual photo so bbox math works */}
                            <Box sx={{
                              aspectRatio: photoAspectRatio,
                              maxWidth: '100%',
                              maxHeight: '100%',
                              position: 'relative',
                            }}>
                              <img
                                src={localImageUrls[photoUrl] || '/image-placeholder.png'}
                                alt={`Defect ${defectIndex + 1} Photo`}
                                style={{ width: '100%', height: '100%', display: 'block' }}
                                onLoad={handleImageLoad}
                                onError={(e) => {
                                  e.target.src = '/image-placeholder.png';
                                  handleImageLoad();
                                }}
                              />
                              {/* Bounding Box Overlay - anchored to inner frame, % math unchanged */}
                              {defect.bounding_box &&
                                ow > 0 && oh > 0 &&
                                defect.bounding_box.x1 != null && defect.bounding_box.y1 != null &&
                                defect.bounding_box.x2 != null && defect.bounding_box.y2 != null &&
                                (() => {
                                  const x1 = parseFloat(defect.bounding_box.x1);
                                  const y1 = parseFloat(defect.bounding_box.y1);
                                  const x2 = parseFloat(defect.bounding_box.x2);
                                  const y2 = parseFloat(defect.bounding_box.y2);
                                  if (x2 <= x1 || y2 <= y1) return null;
                                  return (
                                    <Box sx={{
                                      position: 'absolute',
                                      left: `${(x1 / ow) * 100}%`,
                                      top: `${(y1 / oh) * 100}%`,
                                      width: `${((x2 - x1) / ow) * 100}%`,
                                      height: `${((y2 - y1) / oh) * 100}%`,
                                      border: '2px solid red',
                                      boxSizing: 'border-box',
                                      pointerEvents: 'none',
                                    }} />
                                  );
                                })()
                              }
                            </Box>
                          </Box>
                          {/* Caption */}
                          <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderTop: '1px solid #ddd', flexShrink: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }} noWrap>
                              {formatCaption(photo.caption) || `Photo`}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })() : (
                      // When no photos are directly related to this defect
                      <Paper variant="outlined" sx={{
                        flex: 1,
                        minHeight: 0,
                        p: 2,
                        bgcolor: '#f9f9f9',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          No photos are directly associated with this defect.
                        </Typography>
                      </Paper>
                    )}
                  </Box>

                  {/* Row 3: Details + Map (+ optional measurements/recommendations), locked to half page */}
                  <Box sx={{
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}>
                  {/* Two-column layout for details and map - height auto-derived from square map column */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 0 }}>
                    {/* Left Column: Defect Details */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, flexShrink: 0 }}>
                        Defect Details:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f9f9f9', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <strong>Defect ID:</strong> {defect.id || 'Not specified'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <strong>Type:</strong> {defect.defect_type_name || 'Not specified'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <strong>Location:</strong> {(() => {
                              // First try to get defect's hierarchical location
                              const defectLocation = getDefectLocation(defect);

                              // If location is "Location not specified", use photo GPS coordinates as fallback
                              if (defectLocation === 'Location not specified' && defectPhotos.length > 0) {
                                const photo = defectPhotos[0];
                                if (photo.latitude && photo.longitude) {
                                  return `GPS: ${parseFloat(photo.latitude).toFixed(6)}, ${parseFloat(photo.longitude).toFixed(6)}`;
                                }
                              }

                              return defectLocation;
                            })()}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <strong>Status:</strong> {defect.status || 'Not specified'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <strong>Detection Date:</strong> {defect.first_detected_at || defect.detected_at || defect.created_at
                              ? new Date(defect.first_detected_at || defect.detected_at || defect.created_at).toLocaleString()
                              : 'Not specified'}
                          </Typography>
                          {/* Severity Level with color indicator */}
                          <Typography sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <strong>Severity:</strong>{' '}
                            {defect.severity_level_name ? (
                              <>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: defect.severity_level_color || '#888',
                                    ml: 0.5
                                  }}
                                />
                                <span style={{ marginLeft: 4 }}>{defect.severity_level_name}</span>
                              </>
                            ) : (
                              'Not rated'
                            )}
                          </Typography>
                          {defect.severity_level_description && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 1, fontStyle: 'italic' }}>
                              {defect.severity_level_description}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '0.75rem' }}>
                            <strong>Description:</strong> {defect.description || 'No description provided'}
                          </Typography>
                          {/* Validation Info */}
                          <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #ccc' }}>
                            <Typography sx={{ fontSize: '0.75rem' }}>
                              <strong>Validated By:</strong> {defect.validated_by_name || 'Not validated'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem' }}>
                              <strong>Validated On:</strong> {defect.created_at
                                ? new Date(defect.created_at).toLocaleString()
                                : 'N/A'}
                            </Typography>
                            {defect.notes && (
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                <strong>Notes:</strong> {defect.notes}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Box>

                    {/* Right Column: Location Map - Paper is a perfect square (aspectRatio 1/1, width 100%) */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, flexShrink: 0 }}>
                        Capture Location:
                      </Typography>
                      <Paper variant="outlined" sx={{
                        p: 0,
                        bgcolor: 'transparent',
                        width: '100%',
                        aspectRatio: '1 / 1',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}>
                        {defectPhotos.length > 0 && defectPhotos[0].latitude && defectPhotos[0].longitude ? (
                          <DefectLocationMap
                            photo={defectPhotos[0]}
                            defect={defect}
                          />
                        ) : (
                          <Box sx={{ p: 2, textAlign: 'center', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              No GPS location available for this photo
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Box>

                  {/* Defect measurements/metadata - if available */}
                  {defect.measurements && Object.keys(defect.measurements).length > 0 && (
                    <Box sx={{ flexShrink: 0 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                        Measurements:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                        <Grid container spacing={2}>
                          {Object.entries(defect.measurements).map(([key, value], index) => (
                            <Grid item xs={6} key={`measurement-${index}`}>
                              <Typography variant="body2">
                                <strong>{key}:</strong> {value}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {/* Recommendations - if available */}
                  {defect.recommendations && (
                    <Box sx={{ flexShrink: 0 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                        Recommendations:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                        <Typography variant="body2">
                          {defect.recommendations}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #ddd' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Page {currentPage} of {finalTotalPages}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Repeated Findings Pages for this defect */}
              {defectRepeatedFindings.length > 0 && Array.from({ length: numRfPages }).map((_, rpIdx) => {
                const pageFindings = defectRepeatedFindings.slice(rpIdx * 2, (rpIdx + 1) * 2);
                const rpPageNumber = currentPage + 1 + rpIdx;

                return (
                  <Paper
                    key={`defect-${defectIndex}-repeated-${rpIdx}`}
                    className="pdf-page-element"
                    sx={{
                      width: '100%',
                      aspectRatio: '210/297',
                      bgcolor: '#ffffff',
                      boxShadow: 3,
                      border: '1px solid #ddd',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                      {/* Header */}
                      <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600, mb: 1, flexShrink: 0 }}>
                        Defect #{defectIndex + 1} — Repeated Findings
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', flexShrink: 0 }}>
                        {defect.defect_type_name || 'Unknown Type'} — {defectRepeatedFindings.length} unique detection{defectRepeatedFindings.length !== 1 ? 's' : ''} from different days
                      </Typography>

                      {/* Render each finding (max 2 per page) */}
                      {pageFindings.map((finding, fIdx) => {
                        const imagePath = finding.image_path?.replace(/\\/g, '/');
                        const normalizedPath = imagePath?.startsWith('/') ? imagePath.slice(1) : imagePath;

                        const rfImgWidth = parseFloat(finding.image_width) || 0;
                        const rfImgHeight = parseFloat(finding.image_height) || 0;
                        // Use true image aspect ratio so bbox overlay aligns correctly
                        const rfAspectRatio = (rfImgWidth > 0 && rfImgHeight > 0)
                          ? `${rfImgWidth} / ${rfImgHeight}`
                          : '16 / 9';

                        // Calculate the global finding index (1-based)
                        const findingIndex = rpIdx * 2 + fIdx + 1;

                        return (
                          <Box key={fIdx} sx={{ mb: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                            {/* Finding label */}
                            <Typography variant="subtitle2" sx={{ color: '#003366', fontWeight: 600, mb: 0.5, flexShrink: 0, fontSize: '0.8rem' }}>
                              {getOrdinal(findingIndex)} Repeated
                            </Typography>
                            {/* Photo with bounding box overlay */}
                            <Box sx={{
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              width: '100%',
                              mb: 0.5,
                              flex: 1,
                              minHeight: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              bgcolor: '#f5f5f5'
                            }}>
                              {/* Inner container preserves true image aspect ratio */}
                              <Box sx={{
                                position: 'relative',
                                width: '100%',
                                maxHeight: '100%',
                                aspectRatio: rfAspectRatio,
                                overflow: 'hidden'
                              }}>
                                <img
                                  src={localImageUrls[normalizedPath] || '/image-placeholder.png'}
                                  alt={`Repeated finding ${fIdx + 1}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
                                  onLoad={handleImageLoad}
                                  onError={(e) => { e.target.src = '/image-placeholder.png'; handleImageLoad(); }}
                                />
                                {/* Orange bounding box overlay — aligned to true image dimensions */}
                                {finding.x1 != null && finding.y1 != null &&
                                 finding.x2 != null && finding.y2 != null &&
                                 rfImgWidth > 0 && rfImgHeight > 0 &&
                                 (parseFloat(finding.x2) > parseFloat(finding.x1)) &&
                                 (parseFloat(finding.y2) > parseFloat(finding.y1)) && (
                                  <Box sx={{
                                    position: 'absolute',
                                    left: `${(parseFloat(finding.x1) / rfImgWidth) * 100}%`,
                                    top: `${(parseFloat(finding.y1) / rfImgHeight) * 100}%`,
                                    width: `${((parseFloat(finding.x2) - parseFloat(finding.x1)) / rfImgWidth) * 100}%`,
                                    height: `${((parseFloat(finding.y2) - parseFloat(finding.y1)) / rfImgHeight) * 100}%`,
                                    border: '2px solid #ff9800',
                                    boxSizing: 'border-box',
                                    pointerEvents: 'none',
                                  }} />
                                )}
                              </Box>
                            </Box>

                            {/* Key info row */}
                            <Box sx={{
                              display: 'flex',
                              gap: 1.5,
                              flexWrap: 'wrap',
                              p: 0.75,
                              bgcolor: '#f9f9f9',
                              borderRadius: 1,
                              border: '1px solid #eee',
                              flexShrink: 0
                            }}>
                              <Typography sx={{ fontSize: '0.7rem' }}>
                                <strong>Date:</strong> {finding.detected_at
                                  ? new Date(finding.detected_at).toLocaleDateString()
                                  : 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem' }}>
                                <strong>Device:</strong> {finding.device_name || finding.device_uuid?.substring(0, 8) || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem' }}>
                                <strong>Confidence:</strong> {finding.confidence_score != null
                                  ? `${(parseFloat(finding.confidence_score) * 100).toFixed(1)}%`
                                  : 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem' }}>
                                <strong>Distance:</strong> {finding.distance_from_confirmed_defect_meters != null
                                  ? `${parseFloat(finding.distance_from_confirmed_defect_meters).toFixed(1)}m`
                                  : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Footer */}
                    <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #ddd', flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Page {rpPageNumber} of {finalTotalPages}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
              </React.Fragment>
            );
          })}

          {/* Page N: Back Cover */}
          <Paper 
            className="pdf-page-element"
            sx={{
              width: '100%',
              aspectRatio: '210/297',
              bgcolor: '#ffffff',
              boxShadow: 3,
              border: '1px solid #ddd',
              overflow: 'hidden',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {/* Back Cover Content */}
            <Box sx={{ 
              p: 4,
              flex: 1,
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#e0e0e0' // Light gray background for distinction
            }}>
              <Typography variant="h4" sx={{ color: '#555', fontWeight: 600, mb: 4, textAlign: 'center' }}>
                End of Report
              </Typography>
              {/* Optional: Add contact info, logo, etc. here */}
              <Typography variant="body2" color="text.secondary">
                Generated by IRIS System
              </Typography>
            </Box>
            
            {/* Footer */}
            <Box sx={{ p: 4, pt: 2, borderTop: '1px solid #ddd' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Report Date: {(reportDate || new Date()).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Page {finalTotalPages} of {finalTotalPages}
                </Typography>
              </Box>
            </Box>
          </Paper>

        </Box>
      </Box>
      
      {/* Error message */}
      {error && (
        <Box sx={{ p: 2, color: 'error.main' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      {/* Image loading progress */}
      {totalImages > 0 && !allImagesLoaded && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Loading images ({imagesLoaded}/{totalImages})...
          </Typography>
        </Box>
      )}
      
      {/* Sticky bottom action bar - persistent download UI anchored to scroll-viewport bottom */}
      {!hideDownloadButton && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            // High zIndex required to sit above Leaflet panes (200-700) and any MUI surfaces below appBar level
            zIndex: 1100,
            mt: 3,
            px: 3,
            py: 1.75,
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            boxShadow: '0 -6px 18px rgba(0, 0, 0, 0.08)',
          }}
        >
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              Report successfully saved to database and downloaded!
            </Alert>
          )}

          {saveError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {saveError}
            </Alert>
          )}

          {/* Progress bar shown during generation or upload */}
          {(generating || savingToDb) && pdfProgress.total > 0 && (
            <Box sx={{ mb: 1.5, maxWidth: 720, mx: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {pdfProgress.phase === 'Preparing...' && 'Preparing report...'}
                  {pdfProgress.phase === 'Rendering' && `Capturing page ${pdfProgress.current}/${pdfProgress.total}`}
                  {pdfProgress.phase === 'Uploading' && `Uploading to server... ${pdfProgress.current}%`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pdfProgress.phase === 'Rendering'
                    ? `${Math.round((pdfProgress.current / pdfProgress.total) * 100)}%`
                    : pdfProgress.phase === 'Uploading'
                    ? `${pdfProgress.current}%`
                    : ''}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  pdfProgress.phase === 'Rendering'
                    ? (pdfProgress.current / pdfProgress.total) * 100
                    : pdfProgress.phase === 'Uploading'
                    ? pdfProgress.current
                    : 0
                }
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {/* Single download button - opens a menu offering "Download Only" or "Download & Save to Reports" */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                (generating || savingToDb)
                  ? <CircularProgress size={20} color="inherit" />
                  : <DownloadIcon />
              }
              endIcon={
                !(generating || savingToDb) ? <KeyboardArrowDownIcon /> : null
              }
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
              disabled={generating || savingToDb || (totalImages > 0 && !allImagesLoaded)}
              aria-haspopup="menu"
              aria-expanded={Boolean(downloadMenuAnchor)}
              sx={{
                py: 1.5,
                px: 4,
                minWidth: 320,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: 2,
              }}
            >
              {savingToDb
                ? (pdfProgress.phase === 'Uploading'
                    ? `Uploading... ${pdfProgress.current}%`
                    : 'Generating...')
                : generating
                ? `Generating... (${pdfProgress.current}/${pdfProgress.total})`
                : 'Download Report'}
            </Button>

            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              slotProps={{
                paper: {
                  elevation: 6,
                  sx: { mt: -1, minWidth: 340, borderRadius: 1.5 },
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setDownloadMenuAnchor(null);
                  handleDownload();
                }}
                sx={{ py: 1.25 }}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Download Only"
                  secondary="Save the PDF to your device"
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDownloadMenuAnchor(null);
                  handleDownloadAndSave();
                }}
                disabled={!assetType}
                sx={{ py: 1.25 }}
              >
                <ListItemIcon>
                  <SaveAltIcon fontSize="small" color={!assetType ? 'disabled' : 'primary'} />
                </ListItemIcon>
                <ListItemText
                  primary="Download & Save to Reports"
                  secondary={!assetType
                    ? 'Asset type required to save'
                    : 'Save to device and add to the Reports library'}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PDFReport;
